import { LinearGradient } from 'expo-linear-gradient';
import { Link, usePathname } from 'expo-router';
import { ChevronLeft, ChevronRight, LogOut, Settings } from 'lucide-react-native';
import { memo, type ReactNode, useCallback, useState } from 'react';
import {
  Image,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
  type ViewStyle,
} from 'react-native';
import {
  type NavItem as NavItemType,
  navSections,
  resolveActiveNavHref,
} from '../../config/navigation';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../contexts/TranslationContext';
import { useNavigateToTab } from '../../hooks/useNavigateToTab';
import { colors } from '../../theme/colors';
import { tokens } from '../../theme/tokens';
import { Avatar } from '../primitives/Avatar';
import { Box } from '../primitives/Box';
import { HStack, VStack } from '../primitives/Stack';
import { Text } from '../primitives/Text';

const NOOP = () => undefined;

interface SidebarProps {
  readonly expanded?: boolean;
  readonly onToggle?: () => void;
  readonly onNavigate?: () => void;
}

interface TooltipWrapperProps {
  label: string;
  visible: boolean;
  children: ReactNode;
}

function isRouteActive(pathname: string | null, href: string) {
  if (!pathname) return false;
  if (pathname === href) return true;
  if (pathname.startsWith(`${href}/`)) return true;
  // Detail screens (e.g. /lead/:id) carry no shared prefix with their owning
  // nav item, so map them back to the parent destination for highlighting.
  return resolveActiveNavHref(pathname) === href;
}

function TooltipWrapper({ label, visible, children }: Readonly<TooltipWrapperProps>) {
  const [hovered, setHovered] = useState(false);

  if (Platform.OS !== 'web' || !visible) {
    return <>{children}</>;
  }

  return (
    <View
      style={styles.tooltipAnchor}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      {children}
      {hovered && (
        <View style={styles.tooltip}>
          <Text size="xs" weight="medium" color={colors.text.primary}>
            {label}
          </Text>
        </View>
      )}
    </View>
  );
}

function Logo({ expanded }: Readonly<{ expanded: boolean }>) {
  const { t } = useTranslation();
  const navigateToTab = useNavigateToTab();
  return (
    <Pressable onPress={() => navigateToTab('/dashboard')}>
      <HStack spacing="sm" align="center" style={styles.logoContainer}>
        <Image
          source={require('../../../assets/icon.png')}
          style={styles.logoBadge}
          resizeMode="contain"
          accessibilityLabel={t('layout.sidebar.brandName')}
        />

        {expanded && (
          <VStack spacing="none">
            <Text variant="body" color={colors.text.primary} weight="bold" size="lg">
              {t('layout.sidebar.brandName')}
            </Text>
            <Text variant="caption" color={colors.text.tertiary}>
              {t('layout.sidebar.brandSubtitle')}
            </Text>
          </VStack>
        )}
      </HStack>
    </Pressable>
  );
}

function NavItemContent({
  item,
  expanded,
  active,
  isHovered,
  navKey,
  label,
  onPress,
}: Readonly<{
  item: NavItemType;
  expanded: boolean;
  active: boolean;
  isHovered: boolean;
  navKey: string;
  label: string;
  onPress?: () => void;
}>) {
  return (
    <Pressable
      testID={`nav_${navKey}_link`}
      accessibilityRole="link"
      accessibilityLabel={label}
      onPress={onPress}
      style={({ pressed }) => [
        styles.navItemPressable,
        (isHovered || pressed) && styles.navItemPressed,
      ]}
    >
      <View style={styles.navItemRow}>
        <View
          style={[
            styles.navItemInner,
            expanded ? styles.navItemExpanded : styles.navItemCollapsed,
            active && styles.navItemActive,
          ]}
        >
          {active && expanded && (
            <LinearGradient
              colors={colors.gradients.activeWash}
              start={{ x: 0, y: 0.5 }}
              end={{ x: 1, y: 0.5 }}
              style={styles.activeGradient}
            />
          )}

          <item.icon
            color={active ? colors.primary.neon : colors.text.tertiary}
            size={20}
            strokeWidth={active ? 2.2 : 1.8}
          />
          {expanded && (
            <Text
              variant="body"
              size="sm"
              color={active ? colors.text.primary : colors.text.secondary}
              weight={active ? 'semibold' : 'medium'}
              style={styles.navLabel}
            >
              {label}
            </Text>
          )}
        </View>
      </View>
    </Pressable>
  );
}

function NavItemLink({
  item,
  expanded,
  active,
  isHovered,
  onPress,
}: Readonly<{
  item: NavItemType;
  expanded: boolean;
  active: boolean;
  isHovered: boolean;
  onPress?: () => void;
}>) {
  const { t } = useTranslation();
  const navKey = item.href.replace(/^\//, '');
  const label = t(item.labelKey);

  const content = (
    <NavItemContent
      item={item}
      expanded={expanded}
      active={active}
      isHovered={isHovered}
      navKey={navKey}
      label={label}
      onPress={onPress}
    />
  );

  if (!expanded) {
    return (
      <TooltipWrapper label={label} visible>
        {content}
      </TooltipWrapper>
    );
  }

  return content;
}

const NavItemButton = memo(function NavItemButton({
  item,
  expanded,
  active,
  onNavigate,
}: Readonly<{
  item: NavItemType;
  expanded: boolean;
  active: boolean;
  onNavigate?: () => void;
}>) {
  const navigateToTab = useNavigateToTab();
  const [isHovered, setIsHovered] = useState(false);

  const handlePointerEnter = Platform.OS === 'web' ? () => setIsHovered(true) : undefined;
  const handlePointerLeave = Platform.OS === 'web' ? () => setIsHovered(false) : undefined;
  const handlePress = useCallback(() => {
    navigateToTab(item.href);
    onNavigate?.();
  }, [navigateToTab, item.href, onNavigate]);

  return (
    <View onPointerEnter={handlePointerEnter} onPointerLeave={handlePointerLeave}>
      <NavItemLink
        item={item}
        expanded={expanded}
        active={active}
        isHovered={isHovered}
        onPress={handlePress}
      />
    </View>
  );
});

NavItemButton.displayName = 'NavItemButton';

function SectionDivider() {
  return <View style={styles.sectionDivider} />;
}

const NavSectionGroup = memo(function NavSectionGroup({
  titleKey,
  items,
  expanded,
  pathname,
  onNavigate,
}: Readonly<{
  titleKey: string;
  items: NavItemType[];
  expanded: boolean;
  pathname: string;
  onNavigate?: () => void;
}>) {
  const { t } = useTranslation();
  return (
    <VStack spacing="xs">
      {expanded && (
        <Text
          variant="caption"
          size="xs"
          color={colors.text.tertiary}
          weight="semibold"
          uppercase
          style={styles.sectionTitle}
        >
          {t(titleKey)}
        </Text>
      )}
      {items.map((item) => (
        <NavItemButton
          key={item.href}
          item={item}
          expanded={expanded}
          active={isRouteActive(pathname, item.href)}
          onNavigate={onNavigate}
        />
      ))}
    </VStack>
  );
});

NavSectionGroup.displayName = 'NavSectionGroup';

const SETTINGS_NAV_ITEM: NavItemType = {
  labelKey: 'layout.sidebar.settings',
  href: '/settings',
  icon: Settings,
};

function LogoutButton({ expanded }: Readonly<{ expanded: boolean }>) {
  const { logout } = useAuth();
  const { t } = useTranslation();
  const logoutLabel = t('layout.sidebar.logout');

  const handleLogout = useCallback(async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, [logout]);

  const content = (
    <Pressable
      testID="nav_logout_button"
      onPress={handleLogout}
      style={({ pressed }) => [styles.navItemPressable, pressed && styles.navItemPressed]}
    >
      <View style={styles.navItemRow}>
        <View
          style={[styles.navItemInner, expanded ? styles.navItemExpanded : styles.navItemCollapsed]}
        >
          <LogOut color={colors.text.tertiary} size={20} strokeWidth={1.8} />
          {expanded && (
            <Text variant="body" size="sm" color={colors.text.tertiary} style={styles.navLabel}>
              {logoutLabel}
            </Text>
          )}
        </View>
      </View>
    </Pressable>
  );

  if (!expanded) {
    return (
      <TooltipWrapper label={logoutLabel} visible>
        {content}
      </TooltipWrapper>
    );
  }

  return content;
}

function UserProfileCollapsed({
  displayName,
  avatarSrc,
}: Readonly<{ displayName: string; avatarSrc?: string }>) {
  return (
    <TooltipWrapper label={displayName} visible>
      <Link href="/settings" asChild>
        <Pressable testID="nav_user_profile_link" style={styles.userProfileCollapsed}>
          <Avatar size="sm" src={avatarSrc} name={displayName} />
        </Pressable>
      </Link>
    </TooltipWrapper>
  );
}

function UserProfileExpanded({
  displayName,
  avatarSrc,
  role,
}: Readonly<{
  displayName: string;
  avatarSrc?: string;
  role: string;
}>) {
  return (
    <Link href="/settings" asChild>
      <Pressable testID="nav_user_profile_link">
        <HStack spacing="sm" align="center" style={styles.userProfileExpanded}>
          <Avatar size="sm" src={avatarSrc} name={displayName} style={styles.userAvatarSpacing} />
          <VStack spacing="none" style={styles.userInfoText}>
            <Text
              variant="body"
              size="sm"
              color={colors.text.primary}
              weight="medium"
              numberOfLines={1}
            >
              {displayName}
            </Text>
            <Text variant="caption" size="xs" color={colors.text.tertiary} numberOfLines={1}>
              {role}
            </Text>
          </VStack>
        </HStack>
      </Pressable>
    </Link>
  );
}

function UserProfile({ expanded }: Readonly<{ expanded: boolean }>) {
  const { backendUser, auth0User } = useAuth();
  const { t } = useTranslation();

  const displayName =
    backendUser?.fullName || auth0User?.name || auth0User?.email || t('common.fallbacks.user');

  const role = backendUser?.role
    ? backendUser.role.replaceAll('_', ' ').replaceAll(/\b\w/g, (c) => c.toUpperCase())
    : t('common.fallbacks.member');

  const avatarSrc = auth0User?.picture;

  if (!expanded) {
    return <UserProfileCollapsed displayName={displayName} avatarSrc={avatarSrc} />;
  }

  return <UserProfileExpanded displayName={displayName} avatarSrc={avatarSrc} role={role} />;
}

function ToggleButton({
  expanded,
  onToggle,
}: Readonly<{ expanded: boolean; onToggle: () => void }>) {
  const { t } = useTranslation();
  const Icon = expanded ? ChevronLeft : ChevronRight;

  return (
    <Pressable
      testID="nav_toggle_button"
      onPress={onToggle}
      style={({ pressed }) => [styles.toggleButton, pressed && styles.toggleButtonPressed]}
      accessibilityRole="button"
      accessibilityLabel={
        expanded ? t('layout.sidebar.collapseSidebar') : t('layout.sidebar.expandSidebar')
      }
    >
      <Icon color={colors.text.secondary} size={18} strokeWidth={1.8} />
    </Pressable>
  );
}

function NavSectionList({
  expanded,
  pathname,
  onNavigate,
}: Readonly<{
  expanded: boolean;
  pathname: string;
  onNavigate?: () => void;
}>) {
  return (
    <VStack spacing="lg" style={styles.navSections}>
      {navSections.map((section, index) => (
        <View key={section.titleKey}>
          {index > 0 && <SectionDivider />}
          <View style={index > 0 ? styles.sectionSpacing : undefined}>
            <NavSectionGroup
              titleKey={section.titleKey}
              items={section.items}
              expanded={expanded}
              pathname={pathname}
              onNavigate={onNavigate}
            />
          </View>
        </View>
      ))}
    </VStack>
  );
}

function SidebarImpl({ expanded = true, onToggle = NOOP, onNavigate }: SidebarProps) {
  const pathname = usePathname() || '/';

  return (
    <View
      style={styles.sidebar}
      {...(Platform.OS === 'web' ? ({ role: 'navigation' } as any) : {})}
    >
      <VStack spacing="none" fill style={styles.topSection}>
        <Box paddingY="sm" paddingX={expanded ? 'lg' : 'sm'} style={styles.logoWrapper}>
          <Logo expanded={expanded} />
        </Box>

        <SectionDivider />

        <ScrollView
          style={styles.navScrollView}
          contentContainerStyle={styles.navScrollContent}
          showsVerticalScrollIndicator={false}
        >
          <NavSectionList expanded={expanded} pathname={pathname} onNavigate={onNavigate} />
        </ScrollView>
      </VStack>

      <VStack spacing="xs" style={styles.bottomSection}>
        <SectionDivider />
        <NavItemButton
          item={SETTINGS_NAV_ITEM}
          expanded={expanded}
          active={isRouteActive(pathname, '/settings')}
          onNavigate={onNavigate}
        />
        <LogoutButton expanded={expanded} />
        <SectionDivider />
        <UserProfile expanded={expanded} />
        <ToggleButton expanded={expanded} onToggle={onToggle} />
      </VStack>
    </View>
  );
}

export const Sidebar = memo(SidebarImpl);
Sidebar.displayName = 'Sidebar';

const styles = StyleSheet.create({
  sidebar: {
    backgroundColor: colors.surface.sidebar,
    borderRightWidth: 1,
    borderRightColor: colors.border.subtle,
    flex: 1,
    overflow: 'hidden',
  },
  topSection: {
    flex: 1,
    minHeight: 0,
    paddingTop: tokens.spacing.sm,
  },
  navScrollView: {
    flex: 1,
    minHeight: 0,
  },
  navScrollContent: {
    paddingVertical: tokens.spacing.lg,
  },
  logoWrapper: {
    minHeight: 68,
    justifyContent: 'center',
  },
  logoContainer: {
    minHeight: 48,
  },
  logoBadge: {
    width: 42,
    height: 42,
    borderRadius: tokens.radius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    // HStack gap is dropped on iOS (Box.supportsGap quirk); explicit
    // marginRight guarantees breathing room from the brand text.
    marginRight: tokens.spacing.md,
  },
  navSections: {
    paddingHorizontal: tokens.spacing.sm,
  },
  sectionTitle: {
    paddingHorizontal: tokens.spacing.sm,
    paddingBottom: tokens.spacing.xs,
    letterSpacing: 1.2,
  },
  sectionSpacing: {
    marginTop: tokens.spacing.sm,
  },
  sectionDivider: {
    height: 1,
    backgroundColor: colors.border.subtle,
    marginHorizontal: tokens.spacing.md,
  },
  navItemPressable: {
    borderRadius: tokens.radius.lg,
  },
  navItemPressed: {
    opacity: 0.86,
  },
  navItemRow: {
    position: 'relative',
    flexDirection: 'row',
    alignItems: 'center',
  },
  navItemInner: {
    position: 'relative',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: tokens.radius.lg,
    height: 42,
    overflow: 'hidden',
  },
  navItemExpanded: {
    paddingHorizontal: tokens.spacing.md,
    flex: 1,
  },
  navItemCollapsed: {
    width: 42,
    justifyContent: 'center',
    alignSelf: 'center',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  navItemActive: {
    backgroundColor: colors.primary.muted,
    borderWidth: 1,
    borderColor: colors.primary.subtle,
  },
  activeGradient: {
    ...StyleSheet.absoluteFillObject,
  },
  navLabel: {
    marginLeft: tokens.spacing.sm,
  },
  bottomSection: {
    paddingHorizontal: tokens.spacing.sm,
    paddingBottom: tokens.spacing.md,
  },
  userProfileCollapsed: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: tokens.spacing.sm,
  },
  userProfileExpanded: {
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: tokens.spacing.sm,
  },
  userAvatarSpacing: {
    // HStack gap is dropped on iOS; explicit marginRight keeps the avatar
    // visually separated from the name + role text.
    marginRight: tokens.spacing.md,
  },
  userInfoText: {
    flex: 1,
    overflow: 'hidden',
  },
  toggleButton: {
    alignItems: 'center',
    justifyContent: 'center',
    height: 36,
    borderRadius: tokens.radius.md,
    marginTop: tokens.spacing.xs,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    backgroundColor: colors.surface.row,
  },
  toggleButtonPressed: {
    backgroundColor: colors.interactive.hover,
  },
  tooltipAnchor: {
    position: 'relative',
  },
  tooltip: {
    position: 'absolute',
    left: '100%',
    top: '50%',
    transform: [{ translateY: -14 }],
    marginLeft: tokens.spacing.sm,
    backgroundColor: colors.surface.row,
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: tokens.spacing.xs,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    zIndex: tokens.zIndex.tooltip,
    ...Platform.select({
      web: { boxShadow: '0 12px 26px rgba(0, 0, 0, 0.28)' } as unknown as ViewStyle,
      default: {},
    }),
  },
});
